import { useEffect, useState } from "react";
import { Loader2, Briefcase, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import AdminShell from "../components/AdminShell";
import { api } from "../lib/api";

const fmt = (ts) => (ts ? new Date(ts).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");
const OUTCOME = {
  strong: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  not_yet: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  running: "bg-neutral-700/40 text-neutral-300 border-neutral-600",
  failed: "bg-red-500/15 text-red-300 border-red-500/30",
};
const EVENT_LABEL = { document_uploaded: "Document uploaded", submitted: "Submitted", scored: "Scored", resubmission_paid: "Resubmission paid (KES 299)", connection_requested: "Connection requested" };

function Stat({ label, value, testid }) {
  return (
    <div className="rounded-xl border border-neutral-800 bg-neutral-900/60 px-4 py-3" data-testid={testid}>
      <p className="text-[11px] font-semibold uppercase tracking-wide text-neutral-500">{label}</p>
      <p className="mt-1 text-2xl font-extrabold text-white">{value}</p>
    </div>
  );
}

function Row({ a }) {
  const [open, setOpen] = useState(false);
  const sc = a.scoring || {};
  const outcome = sc.status === "done" ? sc.outcome : sc.status;
  return (
    <>
      <tr className="border-t border-neutral-800 hover:bg-neutral-900/40" data-testid={`fapp-row-${a.application_id}`}>
        <td className="px-3 py-2.5">
          <p className="font-semibold text-white">{a.company_name || "—"}</p>
          <p className="text-xs text-neutral-400">{a.user_name || ""} · {a.user_email}</p>
        </td>
        <td className="px-3 py-2.5">
          <p className="text-neutral-100">{a.funder_name}</p>
          <p className="text-xs text-neutral-500">{a.funder_category} · {a.pack} pack</p>
        </td>
        <td className="px-3 py-2.5 text-neutral-300">{(a.documents || []).length} docs · {a.submission_count} submission{a.submission_count === 1 ? "" : "s"}</td>
        <td className="px-3 py-2.5">
          {sc.status === "done" ? <span className="text-lg font-extrabold text-white" data-testid={`fapp-score-${a.application_id}`}>{sc.score}</span> : <span className="text-neutral-500">—</span>}
          {outcome && <span className={`ml-2 rounded-full border px-2 py-0.5 text-[10px] font-bold ${OUTCOME[outcome] || OUTCOME.running}`}>{outcome === "not_yet" ? "Not yet" : outcome}</span>}
        </td>
        <td className="px-3 py-2.5 text-xs text-neutral-300">{a.connection_requested_at ? <span className="font-bold text-emerald-300">Requested {fmt(a.connection_requested_at)}</span> : "—"}</td>
        <td className="px-3 py-2.5 text-xs text-neutral-400">{fmt(a.last_submitted_at)}</td>
        <td className="px-3 py-2.5 text-right">
          <button type="button" onClick={() => setOpen(!open)} className="rounded p-1 text-neutral-400 hover:text-white" data-testid={`fapp-toggle-${a.application_id}`}>{open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}</button>
        </td>
      </tr>
      {open && (
        <tr className="border-t border-neutral-800/60 bg-neutral-950/60" data-testid={`fapp-detail-${a.application_id}`}>
          <td colSpan={7} className="px-4 py-4">
            <div className="grid gap-5 md:grid-cols-3">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wide text-neutral-500">Documents</p>
                <ul className="mt-1.5 space-y-1 text-xs text-neutral-300">{(a.documents || []).map((d, i) => <li key={i}>{d.filename} <span className="text-neutral-500">({d.slot})</span></li>)}</ul>
              </div>
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wide text-neutral-500">Gaps / strengths</p>
                {sc.gaps?.length ? <ul className="mt-1.5 list-disc space-y-1 pl-4 text-xs text-amber-200">{sc.gaps.map((g, i) => <li key={i}>{g.text}{g.tool ? <span className="text-neutral-500"> → {g.tool.label}</span> : null}</li>)}</ul> : <p className="mt-1.5 text-xs text-neutral-500">—</p>}
                {sc.strengths?.length ? <ul className="mt-2 list-disc space-y-1 pl-4 text-xs text-emerald-200">{sc.strengths.map((s, i) => <li key={i}>{s}</li>)}</ul> : null}
              </div>
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wide text-neutral-500">Activity log</p>
                <ul className="mt-1.5 space-y-1 text-xs text-neutral-300">
                  {(a.events || []).map((e) => <li key={e.event_id}><span className="text-neutral-500">{fmt(e.at)}</span> · {EVENT_LABEL[e.kind] || e.kind}{e.slot ? ` — ${e.slot}` : ""}{e.kind === "scored" && e.score != null ? ` — ${e.score}` : ""}</li>)}
                </ul>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

export default function AdminFunderApplications() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/funder-applications").then((r) => setData(r.data)).catch(() => toast.error("Couldn't load applications")); }, []);
  return (
    <AdminShell current="/admin/funder-applications">
      <div className="mb-6" data-testid="admin-funder-applications-page">
        <div className="flex items-center gap-2"><Briefcase className="h-5 w-5 text-[#E3B84C]" /><h1 className="text-xl font-extrabold text-white">Funder applications</h1></div>
        <p className="mt-1 text-sm text-neutral-400">Direct-pathway applications: documents submitted per funder, Capital Readiness Score on the pack, outcome and connection requests.</p>
      </div>
      {!data ? <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#0A8B85]" /></div> : (
        <>
          <div className="mb-5 grid grid-cols-3 gap-3">
            <Stat label="Applications" value={data.total} testid="fapp-stat-total" />
            <Stat label="Strong candidates" value={data.strong} testid="fapp-stat-strong" />
            <Stat label="Connection requests" value={data.connections} testid="fapp-stat-connections" />
          </div>
          <div className="overflow-x-auto rounded-xl border border-neutral-800">
            <table className="w-full text-left text-sm" data-testid="fapp-table">
              <thead className="bg-neutral-900 text-[11px] font-semibold uppercase tracking-wide text-neutral-400">
                <tr><th className="px-3 py-2.5">Founder</th><th className="px-3 py-2.5">Funder</th><th className="px-3 py-2.5">Submission</th><th className="px-3 py-2.5">CRS</th><th className="px-3 py-2.5">Connection</th><th className="px-3 py-2.5">Last submitted</th><th /></tr>
              </thead>
              <tbody>
                {data.applications.length === 0 ? <tr><td colSpan={7} className="px-3 py-10 text-center text-neutral-500">No applications submitted yet.</td></tr>
                  : data.applications.map((a) => <Row key={a.application_id} a={a} />)}
              </tbody>
            </table>
          </div>
        </>
      )}
    </AdminShell>
  );
}
